import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { AssistantSwitchControl } from '../components/whatsapp/inbox/AssistantSwitchControl';
import { ConversationListPanel } from '../components/whatsapp/inbox/ConversationListPanel';
import { ContextPanel } from '../components/whatsapp/inbox/ContextPanel';
import { ContextSheet } from '../components/whatsapp/inbox/ContextSheet';
import { ThreadPanel } from '../components/whatsapp/inbox/ThreadPanel';
import { canSend as canSendNow, composerState } from '../whatsapp/composer';
import { windowRemaining } from '../whatsapp/clock';
import { useInbox } from '../whatsapp/InboxProvider';
import { useWhatsApp } from '../whatsapp/WhatsAppContext';

const outlineButton =
  'inline-flex h-11 items-center justify-center rounded-full border border-blue-600 bg-white px-5 text-sm font-semibold text-blue-700 no-underline hover:bg-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600';
const solidButton =
  'inline-flex h-11 items-center justify-center rounded-full bg-blue-600 px-5 text-sm font-semibold text-white no-underline hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600';

const ChatIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 11.5a8.4 8.4 0 0 1-12.2 7.5L3 20l1-4.6A8.5 8.5 0 1 1 21 11.5z" />
  </svg>
);

const GearIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
  </svg>
);

export const WhatsAppInbox = () => {
  const { loaded, enabled, account, refreshAccount } = useWhatsApp();
  const inbox = useInbox();
  const [params, setParams] = useSearchParams();
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [contextOpen, setContextOpen] = useState(false);
  const contextOpenerRef = useRef<HTMLElement | null>(null);

  const selectedId = useMemo(() => {
    const n = Number(params.get('c'));
    return Number.isInteger(n) && n > 0 ? n : null;
  }, [params]);

  // The account is fetched once per session: refresh it so the connection, the
  // assistant switch and the active topics shown here are current.
  useEffect(() => {
    void refreshAccount();
  }, [refreshAccount]);

  const { setPageActive, open } = inbox;
  useEffect(() => {
    if (!enabled) return;
    setPageActive(true);
    return () => setPageActive(false);
  }, [enabled, setPageActive]);

  useEffect(() => {
    if (enabled) open(selectedId);
  }, [enabled, selectedId, open]);

  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  // The narrow-screen context sheet belongs to one conversation.
  useEffect(() => {
    setContextOpen(false);
  }, [selectedId]);

  // A conversation that no longer exists: back to "select a conversation".
  useEffect(() => {
    if (inbox.detailState === 'notfound' && selectedId !== null) setParams({}, { replace: true });
  }, [inbox.detailState, selectedId, setParams]);

  if (loaded && !enabled) return <Navigate to="/home" replace />;

  const openContext = () => {
    contextOpenerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setContextOpen(true);
  };
  const closeContext = () => {
    setContextOpen(false);
    contextOpenerRef.current?.focus();
  };

  const select = (id: number) => setParams({ c: String(id) });
  const clearSelection = () => setParams({});

  const unanswered = inbox.counters?.unanswered ?? 0;
  const oldestUnanswered = inbox.list
    .filter((c) => c.awaiting_reply)
    .sort((a, b) => Date.parse(a.last_message_at ?? '') - Date.parse(b.last_message_at ?? ''))[0];

  const selectedSummary =
    (inbox.detail && inbox.detail.conversation.id === selectedId ? inbox.detail.conversation : null) ??
    inbox.list.find((c) => c.id === selectedId) ??
    null;
  const detail = inbox.detail && inbox.detail.conversation.id === selectedId ? inbox.detail : null;

  const composer = composerState({
    assignedTo: selectedSummary?.assigned_to ?? 'assistant',
    canReply: detail ? detail.can_reply : null,
    windowOpen: windowRemaining(selectedSummary?.window.closes_at ?? null, nowMs).open,
    accountConnected: account?.status === undefined || account.status === 'connected',
    contactName: (selectedSummary?.patient?.full_name || selectedSummary?.contact_name || '').split(' ')[0],
  });
  const draft = selectedId !== null ? inbox.draftText(selectedId) : '';
  const sending = selectedId !== null && inbox.sendingIds.has(selectedId);
  const actionError = inbox.actionError && inbox.actionError.conversationId === selectedId ? inbox.actionError.text : null;

  const disconnected = account !== null && account.status !== undefined && account.status !== 'connected';
  const noConversationsAtAll =
    !inbox.listLoading &&
    !inbox.listError &&
    inbox.list.length === 0 &&
    inbox.view.filter === 'all' &&
    !inbox.view.unanswered &&
    inbox.view.q.trim() === '';

  return (
    <div
      className={`flex w-full flex-col bg-stone-50 font-geist text-stone-800 lg:h-screen lg:overflow-hidden lg:p-6 ${
        selectedId !== null ? 'h-dvh overflow-hidden' : 'min-h-screen p-6'
      }`}
    >
      {/* On a narrow screen an open conversation takes the whole screen. */}
      <div className={selectedId !== null ? 'hidden lg:block' : undefined}>
        <Navbar showLogout={true} />
      </div>
      <main
        className={`mx-auto flex w-full max-w-[1312px] flex-1 flex-col lg:min-h-0 lg:gap-5 lg:py-6 ${
          selectedId !== null ? 'min-h-0' : 'gap-5 py-6'
        }`}
      >
        {disconnected && (
          <div
            role="alert"
            className={`flex-wrap items-center justify-between gap-4 rounded-2xl border border-red-200 bg-red-100 p-4 text-red-900 ${
              selectedId !== null ? 'hidden lg:flex' : 'flex'
            }`}
          >
            <div>
              <p className="text-[15px] font-semibold">WhatsApp está desconectado</p>
              <p className="mt-1 text-sm">
                {account?.status_detail
                  ? `No se reciben ni se envían mensajes. ${account.status_detail}.`
                  : 'No se reciben ni se envían mensajes. Hay que volver a conectar el número.'}
              </p>
            </div>
            <Link to="/whatsapp/settings" className={solidButton}>
              Volver a conectar
            </Link>
          </div>
        )}

        {inbox.status === 'offline' && !disconnected && (
          <div role="status" className="rounded-2xl border border-amber-200 bg-amber-100 px-4 py-3 text-sm text-amber-900">
            Sin conexión con el servidor. Reintentando automáticamente.
          </div>
        )}
        {inbox.status === 'stopped' && (
          <div role="alert" className="rounded-2xl border border-red-200 bg-red-100 px-4 py-3 text-sm text-red-900">
            La sesión terminó o la suscripción no está activa. Recarga la página para volver a intentar.
          </div>
        )}

        <div
          className={`flex-wrap items-start justify-between gap-4 ${selectedId !== null ? 'hidden lg:flex' : 'flex'}`}
        >
          <div>
            <h1 className="text-[26px] font-bold tracking-tight text-stone-900 lg:text-[30px]">Bandeja</h1>
            <p className="mt-1 text-[15px] text-stone-600 max-lg:hidden">Conversaciones de WhatsApp de tu clínica.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {account?.display_phone && (
              <span className="inline-flex h-10 items-center gap-2 rounded-full border border-stone-200 bg-white px-4 font-mono text-[13px] text-stone-800 max-lg:hidden">
                <span
                  className={`h-2 w-2 rounded-full ${disconnected ? 'bg-red-600' : 'bg-green-600'}`}
                  aria-hidden="true"
                />
                {disconnected ? <span className="text-red-700">Desconectado</span> : account.display_phone}
              </span>
            )}
            <AssistantSwitchControl />
            <Link
              to="/whatsapp/settings/assistant"
              aria-label="Configuración de WhatsApp"
              title="Configuración de WhatsApp"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-stone-200 bg-white text-stone-700 hover:bg-stone-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600"
            >
              <GearIcon />
            </Link>
          </div>
        </div>

        {(inbox.notifications === 'default' || inbox.notifications === 'denied') && (
          <div
            className={`min-h-10 flex-wrap items-center gap-3 text-sm text-stone-600 ${
              selectedId !== null ? 'hidden lg:flex' : 'flex'
            }`}
          >
            {inbox.notifications === 'default' && (
              <button
                type="button"
                onClick={inbox.requestNotifications}
                className="inline-flex h-10 items-center rounded-full border border-stone-200 bg-white px-4 text-sm font-semibold text-stone-700 hover:bg-stone-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600"
              >
                Avisa cuando me asignen una conversación
              </button>
            )}
            {inbox.notifications === 'denied' && (
              <span>Los avisos están bloqueados en este navegador. Actívalos en los permisos del sitio.</span>
            )}
          </div>
        )}

        <div
          // No `flex-wrap` with a conversation open: a wrapping row lets the thread grow to its content
          // and pushes the composer off the screen.
          className={`flex flex-1 lg:min-h-0 lg:flex-nowrap lg:gap-5 ${
            selectedId !== null ? 'min-h-0 flex-nowrap' : 'min-h-[640px] flex-wrap gap-5'
          }`}
        >
          <div
            className={`min-h-[480px] w-full flex-col lg:flex lg:w-auto lg:basis-[320px] lg:max-w-[360px] ${
              selectedId !== null ? 'hidden' : 'flex'
            }`}
          >
            <ConversationListPanel
              conversations={inbox.list}
              selectedId={selectedId}
              view={inbox.view}
              counters={inbox.counters}
              loading={inbox.listLoading}
              error={inbox.listError}
              hasMore={inbox.hasMore}
              timeZone={inbox.timeZone}
              nowMs={nowMs}
              onFilter={inbox.setFilter}
              onUnanswered={inbox.setUnanswered}
              onQuery={inbox.setQuery}
              onSelect={select}
              onLoadMore={inbox.loadMore}
              onRetry={inbox.reloadList}
            />
          </div>

          <section
            aria-label="Conversación"
            className={`min-h-0 min-w-0 flex-[3] basis-[440px] flex-col lg:flex ${selectedId === null ? 'hidden' : 'flex'}`}
          >
            {selectedId !== null && selectedSummary ? (
              <ThreadPanel
                conversation={selectedSummary}
                detail={detail}
                items={inbox.thread}
                hasMore={inbox.threadHasMore}
                loading={inbox.threadLoading}
                timeZone={inbox.timeZone}
                nowMs={nowMs}
                onLoadOlder={inbox.loadOlder}
                composer={composer}
                draft={draft}
                onDraftChange={(text) => inbox.setDraftText(selectedId, text)}
                canSend={canSendNow(composer, draft, sending)}
                sending={sending}
                onSend={() => void inbox.send(selectedId)}
                retryingIds={inbox.retryingIds}
                onRetry={(messageId) => void inbox.retry(selectedId, messageId)}
                assigning={inbox.assigning}
                onTake={() => void inbox.assign(selectedId, 'human-agent')}
                onReturn={() => void inbox.assign(selectedId, 'assistant')}
                actionError={actionError}
                onBack={clearSelection}
                onOpenContext={openContext}
              />
            ) : selectedId !== null && inbox.detailState === 'error' ? (
              <div className="flex h-full min-h-[640px] lg:min-h-0 flex-col items-center justify-center gap-3 rounded-[20px] border border-stone-200 bg-white p-12 text-center">
                <h2 className="text-lg font-semibold text-stone-900">No se pudo cargar la conversación</h2>
                <button type="button" onClick={() => inbox.open(selectedId, true)} className={outlineButton}>
                  Reintentar
                </button>
              </div>
            ) : selectedId !== null ? (
              <div className="min-h-[640px] animate-pulse lg:h-full lg:min-h-0 rounded-[20px] border border-stone-200 bg-white" aria-busy="true" />
            ) : (
              <div className="flex h-full min-h-[640px] lg:min-h-0 flex-col items-center justify-center gap-3.5 rounded-[20px] border border-stone-200 bg-white p-12 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-stone-100 text-stone-600">
                  <ChatIcon />
                </span>
                {noConversationsAtAll ? (
                  <>
                    <h2 className="font-fraunces text-[26px] font-medium text-stone-900">Todavía no hay conversaciones</h2>
                    <p className="max-w-[420px] text-sm text-stone-600">
                      {account?.display_phone
                        ? `Cuando un paciente escriba a ${account.display_phone}, la conversación aparecerá aquí y el asistente responderá según los temas que configuraste.`
                        : 'Cuando un paciente escriba, la conversación aparecerá aquí.'}
                    </p>
                    <Link to="/whatsapp/settings/assistant" className={outlineButton}>
                      Revisar temas del asistente
                    </Link>
                  </>
                ) : (
                  <>
                    <h2 className="font-fraunces text-[26px] font-medium text-stone-900">Selecciona una conversación</h2>
                    <p className="max-w-[420px] text-sm text-stone-600">
                      Elige una conversación de la lista para leerla.
                      {unanswered > 0 && ` Tienes ${unanswered} sin responder.`}
                    </p>
                    {oldestUnanswered && (
                      <button type="button" onClick={() => select(oldestUnanswered.id)} className={solidButton}>
                        Abrir la más antigua sin responder
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </section>

          <aside aria-label="Contexto" className="hidden min-h-0 w-full basis-[280px] flex-col gap-5 overflow-y-auto lg:flex lg:max-w-[320px]">
            <ContextPanel conversation={selectedSummary} detail={detail} timeZone={inbox.timeZone} nowMs={nowMs} />
          </aside>
        </div>
      </main>

      {contextOpen && selectedId !== null && (
        <ContextSheet onClose={closeContext}>
          <div className="flex flex-col gap-5">
            <ContextPanel conversation={selectedSummary} detail={detail} timeZone={inbox.timeZone} nowMs={nowMs} />
          </div>
        </ContextSheet>
      )}
    </div>
  );
};
